import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useRef, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import notifications from 'notifications';
import {FilterKey, GLOBAL_SEARCH_URL, Operator, SortKey, keyToEmptyFilter} from 'search/SearchConstants';
import {switchToStudyItemsTab} from 'studies/workspace/StudySearchHelpers';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextValue from 'ui-library/TextValue';
import * as DateUtil from 'util/DateUtil';
import * as net from 'util/Net';

const NUM_ANALYSES_CHIPS_TO_DISPLAY = 3;

type AnalysesData =
  | {status: 'loading'}
  | {status: 'error'}
  | {status: 'success'; analyses: Array<AnalysisGraphQL>; totalAnalyses: number};

export function PresentationalStudyAnalysisListView(props: {
  studyId: string;
  data: AnalysesData;
}): JSX.Element {
  if (props.data.status === 'error') {
    return (
      <div className={css(styles.text)}>
        <TextValue value="Unable to fetch analyses. Please refresh the page." isFaded={true} />
      </div>
    );
  } else if (props.data.status === 'loading') {
    return <CenteredHorizontalThrobber />;
  }
  return (
    <div
      data-test-component="StudyAnalysisListView"
      data-test-element="chip-list"
      className={css(styles.section)}
    >
      <FieldLabel className={css(styles.text)} label="Recently created" />
      {props.data.analyses.map((analysis, index) => (
        <div key={index} className={css(styles.chips)}>
          <BenchChip className={css(styles.analysisChip)} id={analysis.id} size={ChipSize.SMALL} />
          <span className={css(styles.createdText)}>
            <TextValue
              value={`Created ${DateUtil.readableTime(analysis.createdAt, {showTime: false})}`}
              isFaded={true}
            />
          </span>
        </div>
      ))}
      {props.data.totalAnalyses === 0 && (
        <div className={css(styles.text)}>
          <TextValue value="This study has no analyses." isFaded={true} />
        </div>
      )}
      {props.data.totalAnalyses > props.data.analyses.length && (
        <div className={css(styles.viewAllButton)}>
          <Button
            size={ButtonSize.SMALL}
            buttonStyle={ButtonStyle.TEXT}
            onClick={async () => {
              await switchToStudyItemsTab(props.studyId, [
                {
                  ...keyToEmptyFilter(FilterKey.TYPES),
                  value: [ModelTypeConstants.ANALYSIS],
                },
              ]);
            }}
            icon="open"
            text={`View all (${props.data.totalAnalyses})`}
          />
        </div>
      )}
    </div>
  );
}

export default function StudyAnalysisListView(props: {studyId: string}): JSX.Element {
  const [data, setData] = useState<AnalysesData>({status: 'loading'});
  const mostRecentSearchIdRef = useRef<string | null>(null);

  useEffect(() => {
    let isUnmounted = false;

    const isStaleSearch = (key: string): boolean => isUnmounted || mostRecentSearchIdRef.current !== key;

    const key = uuidv4();
    mostRecentSearchIdRef.current = key;
    net
      .postJSON({
        url: GLOBAL_SEARCH_URL,
        data: {
          query: '',
          filters: [
            {key: FilterKey.IN_STUDY, operator: Operator.IS, value: [props.studyId]},
            {key: FilterKey.TYPES, operator: Operator.IS_ONE_OF, value: [ModelTypeConstants.ANALYSIS]},
            {key: FilterKey.ARCHIVE_PURPOSES, operator: Operator.IS_ONE_OF, value: ['NOT_ARCHIVED']},
          ],
          limit: NUM_ANALYSES_CHIPS_TO_DISPLAY,
          offset: 0,
          sorts: [{key: SortKey.SORT_CREATED_AT_KEY, reverse: true}],
        },
      })
      .done((resp, status) => {
        if (isStaleSearch(key)) {
          return;
        }
        setData({status: 'success', analyses: Object.values(resp.analysesById), totalAnalyses: resp.total});
      })
      .fail((resp) => {
        if (isStaleSearch(key)) {
          return;
        }
        setData({status: 'error'});
        notifications.notifyError(resp);
      })
      .always(() => {
        if (isStaleSearch(key)) {
          return;
        }
        mostRecentSearchIdRef.current = null;
      });
    setData({status: 'loading'});
    return () => {
      isUnmounted = true;
    };
  }, [props.studyId]);

  return <PresentationalStudyAnalysisListView studyId={props.studyId} data={data} />;
}

const styles = StyleSheet.create({
  text: {
    marginLeft: '15px',
  },
  analysisChip: {
    marginLeft: '15px',
    marginBottom: '5px',
  },
  viewAllButton: {
    marginLeft: '10px',
  },
  section: {
    marginLeft: '45px',
  },
  chips: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  createdText: {
    fontVariantNumeric: 'tabular-nums',
    flex: 'none',
  },
});
